import Carregando from '../components/Carregando';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, TextField, Autocomplete, Button, Alert, MenuItem,
} from '@mui/material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AcessoNegado from '../components/AcessoNegado';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

// ─── Cores e textos ──────────────────────────────────────────────────────────
const NAVY = '#13375F';
const NAVY_ESCURO = '#0d2642';
const TINTA = '#1a1a2e';
const TINTA_2 = '#5b6472';
const TINTA_3 = '#8a93a0';
const GRADE = '#e9ecf0';
const LINHA_BASE = '#cfd5dc';

// Situação (verde / amarelo / vermelho). "hero" = versão mais clara para fundo escuro.
const STATUS = {
  verde:    { label: 'No ritmo', cor: '#16A34A', hero: '#22C55E' },
  amarelo:  { label: 'Atenção',  cor: '#D97706', hero: '#F59E0B' },
  vermelho: { label: 'Crítico',  cor: '#DC2626', hero: '#EF4444' },
};

// Categorias da Agenda no dashboard (ordem fixa; a cor segue a categoria, nunca a posição)
const CATEGORIAS = [
  { chave: 'consultoria',     label: 'Consultoria',     cor: '#2a78d6', textoNaBarra: '#ffffff' },
  { chave: 'aps_comercial',   label: 'APS e Comercial', cor: '#eb6834', textoNaBarra: '#ffffff' },
  { chave: 'interno',         label: 'Interno',         cor: '#4a3aa7', textoNaBarra: '#ffffff' },
  { chave: 'desenvolvimento', label: 'Desenvolvimento', cor: '#eda100', textoNaBarra: '#1a1a2e' },
  { chave: 'logistica',       label: 'Logística',       cor: '#e87ba4', textoNaBarra: '#1a1a2e' },
];
const SEM_REGISTRO = { label: 'Sem registro', cor: '#dfe3e8' };

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const MESES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// ─── Formatação ──────────────────────────────────────────────────────────────
const fmtPct = (v) => (v === null || v === undefined
  ? '—'
  : `${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`);
const fmtDias = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
const chavePeriodo = (ano, mes) => `${ano}-${String(mes).padStart(2, '0')}`;
const rotuloPeriodoLongo = (chave) => { const [a, m] = chave.split('-'); return `${MESES[Number(m) - 1]}/${a}`; };
const rotuloMesCurto = (ano, mes) => `${MESES_CURTO[mes - 1]}/${String(ano).slice(2)}`;

// ─── Pequenas peças ──────────────────────────────────────────────────────────
function IconeStatus({ status, tamanho = 14 }) {
  const cor = STATUS[status]?.cor || TINTA_3;
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="8" cy="8" r="8" fill={cor} />
      {status === 'verde' && <path d="M4.6 8.3l2.2 2.2 4.6-4.9" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />}
      {status === 'amarelo' && <path d="M8 4.2v4.4M8 11.1v.1" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />}
      {status === 'vermelho' && <path d="M5.2 5.2l5.6 5.6M10.8 5.2l-5.6 5.6" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />}
    </svg>
  );
}

function CartaoGrafico({ titulo, subtitulo, legenda, children }) {
  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: TINTA }}>{titulo}</Typography>
        {subtitulo && <Typography sx={{ fontSize: 12, color: TINTA_3, mt: 0.2 }}>{subtitulo}</Typography>}
        {legenda && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.8, mt: 1.4 }}>{legenda}</Box>}
        <Box sx={{ mt: 2 }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

function ItemLegenda({ cor, texto, status }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
      {status
        ? <IconeStatus status={status} tamanho={13} />
        : <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: cor, flexShrink: 0 }} />}
      <Typography sx={{ fontSize: 12, color: TINTA_2 }}>{texto}</Typography>
    </Box>
  );
}

// ─── Faixa principal: Taxa de Ocupação em destaque ───────────────────────────
function Destaque({ resumo, metas, periodoTexto, escopoTexto }) {
  const taxa = resumo.ocupacao;
  const st = resumo.status ? STATUS[resumo.status] : null;
  const diferenca = taxa === null ? null : taxa - metas.verde;

  return (
    <Box sx={{
      position: 'relative', borderRadius: 3, overflow: 'hidden', mb: 2.5,
      backgroundImage: `
        repeating-linear-gradient(45deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 26px),
        linear-gradient(135deg, ${NAVY} 0%, ${NAVY_ESCURO} 100%)
      `,
      p: { xs: 2.5, md: 4 },
    }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 3, md: 6 }, alignItems: 'center' }}>
        {/* número principal */}
        <Box sx={{ minWidth: 250 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600 }}>Taxa de Ocupação</Typography>
          <Typography sx={{ color: 'white', fontWeight: 800, lineHeight: 1.05, fontSize: { xs: 56, md: 78 }, letterSpacing: '-0.02em', my: 0.5 }}>
            {fmtPct(taxa)}
          </Typography>
          {st ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 1.4, py: 0.5, borderRadius: 5, backgroundColor: 'rgba(255,255,255,0.1)', border: `1px solid ${st.hero}` }}>
              <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
                <circle cx="8" cy="8" r="8" fill={st.hero} />
                {resumo.status === 'verde' && <path d="M4.6 8.3l2.2 2.2 4.6-4.9" fill="none" stroke="#0d2642" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />}
                {resumo.status === 'amarelo' && <path d="M8 4.2v4.4M8 11.1v.1" fill="none" stroke="#0d2642" strokeWidth="1.9" strokeLinecap="round" />}
                {resumo.status === 'vermelho' && <path d="M5.2 5.2l5.6 5.6M10.8 5.2l-5.6 5.6" fill="none" stroke="#0d2642" strokeWidth="1.8" strokeLinecap="round" />}
              </svg>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 13 }}>{st.label}</Typography>
            </Box>
          ) : (
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>Sem dados no período</Typography>
          )}
          {diferenca !== null && (
            <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: 13, mt: 1.2 }}>
              {diferenca >= 0
                ? `${fmtDias(Math.abs(diferenca))} pontos acima da meta de ${fmtDias(metas.verde)}%`
                : `${fmtDias(Math.abs(diferenca))} pontos abaixo da meta de ${fmtDias(metas.verde)}%`}
            </Typography>
          )}
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, mt: 0.6 }}>{periodoTexto} · {escopoTexto}</Typography>
        </Box>

        {/* barra de meta + situação dos consultores */}
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Box sx={{ position: 'relative', pt: 3.2, pb: 0.5 }}>
            <Typography sx={{ position: 'absolute', top: 0, left: `${metas.verde}%`, transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.88)', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
              Meta {fmtDias(metas.verde)}%
            </Typography>
            <Box sx={{ position: 'relative', height: 14, borderRadius: 7, backgroundColor: 'rgba(255,255,255,0.14)', overflow: 'hidden' }}>
              {taxa !== null && (
                <Box sx={{ height: '100%', width: `${Math.min(taxa, 100)}%`, backgroundColor: st.hero, borderRadius: 7, transition: 'width 0.4s ease' }} />
              )}
            </Box>
            <Box sx={{ position: 'absolute', top: 22, left: `${metas.amarelo}%`, width: 2, height: 24, backgroundColor: 'rgba(255,255,255,0.55)', transform: 'translateX(-1px)' }} />
            <Box sx={{ position: 'absolute', top: 22, left: `${metas.verde}%`, width: 2, height: 24, backgroundColor: 'rgba(255,255,255,0.9)', transform: 'translateX(-1px)' }} />
            <Typography sx={{ position: 'absolute', top: 47, left: `${metas.amarelo}%`, transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
              Atenção {fmtDias(metas.amarelo)}%
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.8 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 10.5 }}>0%</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 10.5 }}>100%</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, md: 3.5 }, mt: 2.2 }}>
            {Object.entries(STATUS).map(([chave, cfg]) => (
              <Box key={chave} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <svg width="22" height="22" viewBox="0 0 16 16" aria-hidden="true">
                  <circle cx="8" cy="8" r="8" fill={cfg.hero} />
                  {chave === 'verde' && <path d="M4.6 8.3l2.2 2.2 4.6-4.9" fill="none" stroke="#0d2642" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />}
                  {chave === 'amarelo' && <path d="M8 4.2v4.4M8 11.1v.1" fill="none" stroke="#0d2642" strokeWidth="1.9" strokeLinecap="round" />}
                  {chave === 'vermelho' && <path d="M5.2 5.2l5.6 5.6M10.8 5.2l-5.6 5.6" fill="none" stroke="#0d2642" strokeWidth="1.8" strokeLinecap="round" />}
                </svg>
                <Box>
                  <Typography sx={{ color: 'white', fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{resumo.por_status[chave]}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 11.5 }}>
                    {cfg.label} · {chave === 'verde' ? `≥ ${fmtDias(metas.verde)}%` : chave === 'amarelo' ? `≥ ${fmtDias(metas.amarelo)}%` : `< ${fmtDias(metas.amarelo)}%`}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Indicadores (uma linha de cartões) ──────────────────────────────────────
function Indicadores({ resumo }) {
  const tem = resumo.dias_uteis > 0;
  const itens = [
    { chave: 'uteis', label: 'Dias úteis', valor: fmtDias(resumo.dias_uteis), sub: `${resumo.n_consultores} consultor${resumo.n_consultores === 1 ? '' : 'es'}`, cor: null },
    ...CATEGORIAS.map((c) => ({
      chave: c.chave, label: c.label, cor: c.cor,
      valor: tem ? fmtPct(resumo.pct[c.chave]) : '—',
      sub: `${fmtDias(resumo.dias[c.chave])} dias`,
    })),
    { chave: 'ocio', label: 'Ociosidade', valor: tem ? fmtPct(resumo.ociosidade) : '—', sub: `${fmtDias(resumo.dias_sem_registro)} dias sem registro`, cor: SEM_REGISTRO.cor },
  ];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1.5, mb: 2.5 }}>
      {itens.map((it) => (
        <Card key={it.chave} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              {it.cor && <Box sx={{ width: 9, height: 9, borderRadius: '2px', backgroundColor: it.cor, border: it.chave === 'ocio' ? '1px solid #c3cad2' : 'none', flexShrink: 0 }} />}
              <Typography sx={{ fontSize: 12.5, color: TINTA_2, fontWeight: 600 }} noWrap>{it.label}</Typography>
            </Box>
            <Typography sx={{ fontSize: 27, fontWeight: 800, color: TINTA, lineHeight: 1.15, mt: 0.6 }}>{it.valor}</Typography>
            <Typography sx={{ fontSize: 11.5, color: TINTA_3, mt: 0.2 }} noWrap>{it.sub}</Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

// ─── Barras horizontais (ranking) ────────────────────────────────────────────
const LARGURA_NOME = { xs: 104, sm: 150 };
const LARGURA_VALOR = 58;

function RankingBarras({ itens, escalaMax = 100, meta, aoMostrar, aoEsconder, aoClicar }) {
  const marcas = [0, 25, 50, 75, 100].filter((t) => t <= escalaMax);
  return (
    <Box sx={{ position: 'relative' }}>
      {/* grade e linha da meta (atrás das barras) */}
      <Box sx={{ position: 'absolute', top: 0, bottom: 22, left: { xs: LARGURA_NOME.xs, sm: LARGURA_NOME.sm }, right: LARGURA_VALOR, pointerEvents: 'none' }}>
        {marcas.map((t) => (
          <Box key={t} sx={{ position: 'absolute', top: 0, bottom: 0, left: `${(t / escalaMax) * 100}%`, width: '1px', backgroundColor: t === 0 ? LINHA_BASE : GRADE }} />
        ))}
        {meta !== undefined && (
          <>
            <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${(meta / escalaMax) * 100}%`, width: '2px', backgroundColor: TINTA_2, opacity: 0.55, transform: 'translateX(-1px)' }} />
          </>
        )}
      </Box>

      {itens.map((it) => (
        <Box
          key={it.id}
          role="button" tabIndex={0}
          onClick={() => aoClicar && aoClicar(it)}
          onKeyDown={(e) => { if (e.key === 'Enter' && aoClicar) aoClicar(it); }}
          onPointerMove={(e) => aoMostrar(e, it.tooltip)}
          onPointerLeave={aoEsconder}
          onFocus={(e) => { const r = e.currentTarget.getBoundingClientRect(); aoMostrar({ clientX: r.left + 160, clientY: r.top }, it.tooltip); }}
          onBlur={aoEsconder}
          sx={{
            position: 'relative', display: 'flex', alignItems: 'center', height: 30, cursor: aoClicar ? 'pointer' : 'default',
            borderRadius: 1, outlineOffset: -2,
            '&:hover': { backgroundColor: 'rgba(19,55,95,0.05)' },
            '&:focus-visible': { outline: `2px solid ${NAVY}` },
          }}
        >
          <Typography noWrap sx={{ width: LARGURA_NOME, flexShrink: 0, fontSize: 12.5, color: TINTA, pr: 1.2 }} title={it.nome}>{it.nome}</Typography>
          <Box sx={{ flex: 1, position: 'relative', height: 18 }}>
            {it.valor !== null && (
              <Box sx={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${Math.max(Math.min((it.valor / escalaMax) * 100, 100), it.valor > 0 ? 0.8 : 0)}%`,
                backgroundColor: it.cor, borderRadius: '0 4px 4px 0',
              }} />
            )}
          </Box>
          <Typography sx={{ width: LARGURA_VALOR, flexShrink: 0, textAlign: 'right', fontSize: 12.5, fontWeight: 700, color: TINTA, fontVariantNumeric: 'tabular-nums' }}>
            {fmtPct(it.valor)}
          </Typography>
        </Box>
      ))}

      {/* escala */}
      <Box sx={{ position: 'relative', height: 22, ml: { xs: `${LARGURA_NOME.xs}px`, sm: `${LARGURA_NOME.sm}px` }, mr: `${LARGURA_VALOR}px` }}>
        {marcas.map((t) => (
          <Typography key={t} sx={{ position: 'absolute', top: 4, left: `${(t / escalaMax) * 100}%`, transform: 'translateX(-50%)', fontSize: 10.5, color: TINTA_3, fontVariantNumeric: 'tabular-nums' }}>{t}%</Typography>
        ))}
        {meta !== undefined && (
          <Typography sx={{ position: 'absolute', top: 4, left: `${(meta / escalaMax) * 100}%`, transform: 'translateX(-50%)', fontSize: 10.5, fontWeight: 700, color: TINTA, backgroundColor: 'white', px: 0.4, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>
            meta {fmtDias(meta)}%
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Destaque: Consultoria por consultor (indicador-chave, medidores em anel) ─
const VERMELHO_LW = '#e94560';

function AnelConsultoria({ item, meta, aoMostrar, aoEsconder, aoClicar }) {
  const raio = 34;
  const circ = 2 * Math.PI * raio;
  const valor = item.valor === null ? 0 : Math.max(0, Math.min(item.valor, 100));
  const gradId = `anel-consultoria-${item.id}`;
  const st = item.status ? STATUS[item.status] : null;
  return (
    <Box
      role="button" tabIndex={0}
      onClick={() => aoClicar && aoClicar(item)}
      onKeyDown={(e) => { if (e.key === 'Enter' && aoClicar) aoClicar(item); }}
      onPointerMove={(e) => aoMostrar(e, item.tooltip)}
      onPointerLeave={aoEsconder}
      onFocus={(e) => { const r = e.currentTarget.getBoundingClientRect(); aoMostrar({ clientX: r.left + 80, clientY: r.top }, item.tooltip); }}
      onBlur={aoEsconder}
      sx={{
        textAlign: 'center', p: 1.4, borderRadius: 2, backgroundColor: '#f4f6f9', cursor: 'pointer',
        transition: 'background-color 0.15s ease', outlineOffset: -2,
        '&:hover': { backgroundColor: '#e9eef5' },
        '&:focus-visible': { outline: `2px solid ${NAVY}` },
      }}
    >
      <Box sx={{ position: 'relative', width: 86, height: 86, mx: 'auto' }}>
        <svg width="86" height="86" viewBox="0 0 86 86" aria-hidden="true">
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor={st ? st.cor : TINTA_3} />
              <stop offset="1" stopColor={st ? st.hero : TINTA_3} />
            </linearGradient>
          </defs>
          <circle cx="43" cy="43" r={raio} fill="none" stroke="#e6eaf0" strokeWidth="8" />
          {valor > 0 && (
            <circle cx="43" cy="43" r={raio} fill="none" stroke={`url(#${gradId})`} strokeWidth="8" strokeLinecap="round"
              strokeDasharray={`${(valor / 100) * circ} ${circ}`} transform="rotate(-90 43 43)" />
          )}
          {/* marca da meta no anel */}
          <g transform={`rotate(${(meta / 100) * 360} 43 43)`}>
            <line x1="43" x2="43" y1={43 - raio - 6} y2={43 - raio + 6} stroke={TINTA} strokeWidth="2.4" strokeLinecap="round" />
          </g>
        </svg>
        <Typography sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 14.5, color: TINTA, fontVariantNumeric: 'tabular-nums' }}>
          {fmtPct(item.valor)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.6, mt: 0.8, minWidth: 0 }}>
        {item.status && <IconeStatus status={item.status} tamanho={12} />}
        <Typography noWrap title={item.nome} sx={{ fontSize: 12.5, fontWeight: 600, color: TINTA }}>{item.nome}</Typography>
      </Box>
      <Typography sx={{ fontSize: 11, color: TINTA_2 }}>{fmtDias(item.dias)} de {fmtDias(item.uteis)} dias</Typography>
      {item.semRegistro > 0 && (
        <Typography sx={{ fontSize: 11, color: '#DC2626', fontWeight: 700 }}>
          {fmtDias(item.semRegistro)} {item.semRegistro === 1 ? 'dia sem registro' : 'dias sem registro'}
        </Typography>
      )}
    </Box>
  );
}

function ConsultoriaPorConsultor({ consultores, resumo, metas, aoMostrar, aoEsconder, aoClicar }) {
  const itens = consultores
    .map((c) => ({
      id: c.id, nome: c.nome, valor: c.pct.consultoria, dias: c.dias.consultoria, uteis: c.dias_uteis, status: c.status, semRegistro: c.dias_sem_registro,
      tooltip: {
        titulo: c.nome,
        linhas: [
          { status: c.status, rotulo: c.status ? STATUS[c.status].label : 'Sem dados', valor: fmtPct(c.pct.consultoria) },
          { rotulo: 'Dias de consultoria', valor: `${fmtDias(c.dias.consultoria)} de ${fmtDias(c.dias_uteis)}` },
          { rotulo: 'Meta', valor: `${fmtDias(metas.verde)}%` },
          { cor: SEM_REGISTRO.cor, rotulo: 'Dias sem registro', valor: `${fmtDias(c.dias_sem_registro)} dias` },
        ],
      },
    }))
    .sort((a, b) => (b.valor ?? -1) - (a.valor ?? -1) || a.nome.localeCompare(b.nome));

  return (
    <Card sx={{ borderRadius: 3, mb: 2, position: 'relative', overflow: 'hidden', backgroundColor: 'white', boxShadow: '0 6px 22px rgba(26,26,46,0.14)' }}>
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, backgroundColor: VERMELHO_LW }} />
      <CardContent sx={{ p: { xs: 2.5, sm: 3 }, pl: { xs: 3, sm: 3.8 }, '&:last-child': { pb: { xs: 2.5, sm: 3 } } }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: 19, sm: 22 }, lineHeight: 1.2, color: TINTA }}>Consultoria por consultor</Typography>
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography sx={{ fontSize: 12, color: TINTA_2, fontWeight: 600 }}>Equipe no período</Typography>
            <Typography sx={{ fontSize: 32, fontWeight: 800, lineHeight: 1.1, color: resumo.status ? STATUS[resumo.status].cor : NAVY, fontVariantNumeric: 'tabular-nums' }}>
              {fmtPct(resumo.pct.consultoria)}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: TINTA_3 }}>{fmtDias(resumo.dias.consultoria)} dias de consultoria</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.8, mb: 2 }}>
          {Object.entries(STATUS).map(([chave, cfg]) => (
            <ItemLegenda key={chave} status={chave}
              texto={`${cfg.label} ${chave === 'verde' ? `≥ ${fmtDias(metas.verde)}%` : chave === 'amarelo' ? `${fmtDias(metas.amarelo)}–${fmtDias(metas.verde)}%` : `< ${fmtDias(metas.amarelo)}%`}`} />
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
            <Box sx={{ width: 3, height: 13, borderRadius: 2, backgroundColor: TINTA, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, color: TINTA_2 }}>Marca no anel = meta de {fmtDias(metas.verde)}%</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.2 }}>
          {itens.map((it) => (
            <AnelConsultoria key={it.id} item={it} meta={metas.verde} aoMostrar={aoMostrar} aoEsconder={aoEsconder} aoClicar={aoClicar} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Composição dos dias (barras empilhadas a 100%) ──────────────────────────
function Composicao({ consultores, aoMostrar, aoEsconder, aoClicar }) {
  return (
    <Box>
      {consultores.map((c) => {
        const ocupados = CATEGORIAS.reduce((s, cat) => s + (c.dias[cat.chave] || 0), 0);
        const base = Math.max(c.dias_uteis, ocupados + c.dias_sem_registro) || 1;
        const segmentos = [
          ...CATEGORIAS.map((cat) => ({ ...cat, dias: c.dias[cat.chave] || 0 })),
          { chave: 'sem_registro', label: SEM_REGISTRO.label, cor: SEM_REGISTRO.cor, textoNaBarra: TINTA_2, dias: c.dias_sem_registro || 0 },
        ].filter((s) => s.dias > 0);
        const tooltip = {
          titulo: c.nome,
          linhas: [
            ...CATEGORIAS.map((cat) => ({ cor: cat.cor, rotulo: cat.label, valor: `${fmtDias(c.dias[cat.chave])} dias · ${fmtPct(c.pct[cat.chave])}` })),
            { cor: SEM_REGISTRO.cor, rotulo: SEM_REGISTRO.label, valor: `${fmtDias(c.dias_sem_registro)} dias · ${fmtPct(c.ociosidade)}` },
          ],
        };
        return (
          <Box
            key={c.id} role="button" tabIndex={0}
            onClick={() => aoClicar(c)}
            onKeyDown={(e) => { if (e.key === 'Enter') aoClicar(c); }}
            onPointerMove={(e) => aoMostrar(e, tooltip)}
            onPointerLeave={aoEsconder}
            onFocus={(e) => { const r = e.currentTarget.getBoundingClientRect(); aoMostrar({ clientX: r.left + 160, clientY: r.top }, tooltip); }}
            onBlur={aoEsconder}
            sx={{
              display: 'flex', alignItems: 'center', height: 30, cursor: 'pointer', borderRadius: 1, outlineOffset: -2,
              '&:hover': { backgroundColor: 'rgba(19,55,95,0.05)' },
              '&:focus-visible': { outline: `2px solid ${NAVY}` },
            }}
          >
            <Typography noWrap sx={{ width: LARGURA_NOME, flexShrink: 0, fontSize: 12.5, color: TINTA, pr: 1.2 }} title={c.nome}>{c.nome}</Typography>
            <Box sx={{ flex: 1, display: 'flex', gap: '2px', height: 20 }}>
              {segmentos.map((s, i) => {
                const pct = (s.dias / base) * 100;
                return (
                  <Box key={s.chave} sx={{
                    width: `${pct}%`, backgroundColor: s.cor, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: i === segmentos.length - 1 ? '0 4px 4px 0' : 0, minWidth: 0,
                  }}>
                    {pct >= 9 && (
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: s.textoNaBarra, lineHeight: 1 }}>{Math.round(pct)}%</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Evolução mensal (linha) ─────────────────────────────────────────────────
function Evolucao({ mensal, metas, aoMostrar, aoEsconder }) {
  const L = 38; const R = 22; const T = 26; const B = 34; const W = 520; const H = 300;
  const valores = mensal.map((m) => m.ocupacao).filter((v) => v !== null);
  const [ativo, setAtivo] = useState(null);

  if (valores.length === 0) {
    return <Typography sx={{ color: TINTA_3, fontSize: 13, py: 6, textAlign: 'center' }}>Ainda não há meses com registro para mostrar a evolução.</Typography>;
  }

  const baixo = Math.max(0, Math.floor((Math.min(...valores, metas.verde) - 10) / 10) * 10);
  const alto = Math.ceil((Math.max(...valores, metas.verde) + 10) / 10) * 10;
  const x = (i) => L + (i * (W - L - R)) / (mensal.length - 1);
  const y = (v) => T + (1 - (v - baixo) / (alto - baixo)) * (H - T - B);

  const marcasY = [];
  for (let t = baixo; t <= alto; t += 10) marcasY.push(t);

  // linha com quebras onde não há dado
  const trechos = [];
  let atual = [];
  mensal.forEach((m, i) => {
    if (m.ocupacao === null) { if (atual.length) trechos.push(atual); atual = []; } else atual.push([x(i), y(m.ocupacao)]);
  });
  if (atual.length) trechos.push(atual);

  const selecionados = mensal.map((m, i) => ({ ...m, i })).filter((m) => m.selecionado && m.ocupacao !== null);
  const comRotulo = selecionados.length <= 4 ? selecionados : selecionados.slice(-1);

  const mover = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    const i = Math.max(0, Math.min(mensal.length - 1, Math.round(((px - L) / (W - L - R)) * (mensal.length - 1))));
    setAtivo(i);
    const m = mensal[i];
    aoMostrar(e, {
      titulo: `${MESES[m.mes - 1]}/${m.ano}`,
      linhas: [
        { traco: NAVY, rotulo: 'Taxa de ocupação', valor: fmtPct(m.ocupacao) },
        { rotulo: 'Dias úteis', valor: fmtDias(m.dias_uteis) },
      ],
    });
  };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Evolução mensal da taxa de ocupação" style={{ display: 'block', touchAction: 'none' }}>
      {marcasY.map((t) => (
        <g key={t}>
          <line x1={L} x2={W - R} y1={y(t)} y2={y(t)} stroke={t === baixo ? LINHA_BASE : GRADE} strokeWidth="1" />
          <text x={L - 8} y={y(t) + 3.5} textAnchor="end" fontSize="10.5" fill={TINTA_3}>{t}%</text>
        </g>
      ))}
      {/* meta */}
      <line x1={L} x2={W - R} y1={y(metas.verde)} y2={y(metas.verde)} stroke={TINTA_2} strokeOpacity="0.55" strokeWidth="2" />
      <text x={W - R} y={y(metas.verde) - 6} textAnchor="end" fontSize="10.5" fontWeight="700" fill={TINTA}>meta {fmtDias(metas.verde)}%</text>

      {ativo !== null && <line x1={x(ativo)} x2={x(ativo)} y1={T} y2={H - B} stroke={LINHA_BASE} strokeWidth="1" />}

      {trechos.map((pts, k) => (
        <polyline key={k} points={pts.map((p) => p.join(',')).join(' ')} fill="none" stroke={NAVY} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      ))}

      {mensal.map((m, i) => (m.ocupacao === null ? null : (
        <circle key={i} cx={x(i)} cy={y(m.ocupacao)} r={m.selecionado ? 5.5 : 4}
          fill={m.selecionado ? NAVY : '#8fa6bf'} stroke="#ffffff" strokeWidth="2" />
      )))}

      {comRotulo.map((m) => (
        <text key={m.i} x={x(m.i)} y={y(m.ocupacao) - 11} textAnchor="middle" fontSize="11" fontWeight="700" fill={TINTA}>{fmtPct(m.ocupacao)}</text>
      ))}

      {mensal.map((m, i) => (
        <text key={i} x={x(i)} y={H - 12} textAnchor="middle" fontSize="10.5" fill={m.selecionado ? TINTA : TINTA_3} fontWeight={m.selecionado ? 700 : 400}>
          {rotuloMesCurto(m.ano, m.mes)}
        </text>
      ))}

      {/* área sensível ao mouse (maior que as marcas) */}
      <rect x={L - 10} y={0} width={W - L - R + 20} height={H - B + 10} fill="transparent"
        onPointerMove={mover} onPointerLeave={() => { setAtivo(null); aoEsconder(); }} />
    </svg>
  );
}

// ─── Dados em tabela (equivalente acessível dos gráficos) ────────────────────
function TabelaDados({ dados, aoClicar }) {
  const th = { textAlign: 'right', padding: '8px 10px', fontSize: 12, color: TINTA_2, fontWeight: 700, whiteSpace: 'nowrap', borderBottom: `1px solid ${LINHA_BASE}` };
  const td = { textAlign: 'right', padding: '7px 10px', fontSize: 12.5, color: TINTA, whiteSpace: 'nowrap', borderBottom: `1px solid ${GRADE}`, fontVariantNumeric: 'tabular-nums' };
  const r = dados.resumo;
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left' }}>Consultor</th>
            <th style={{ ...th, textAlign: 'left' }}>Célula</th>
            <th style={th}>Dias úteis</th>
            {CATEGORIAS.map((c) => <th key={c.chave} style={th}>{c.label}</th>)}
            <th style={th}>Sem registro</th>
            <th style={th}>Ocupação</th>
            <th style={th}>Ociosidade</th>
            <th style={{ ...th, textAlign: 'left' }}>Situação</th>
          </tr>
        </thead>
        <tbody>
          {dados.consultores.map((c) => (
            <tr key={c.id} onClick={() => aoClicar(c)} style={{ cursor: 'pointer' }}>
              <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{c.nome}</td>
              <td style={{ ...td, textAlign: 'left', color: TINTA_2 }}>{c.celula || '—'}</td>
              <td style={td}>{fmtDias(c.dias_uteis)}</td>
              {CATEGORIAS.map((cat) => <td key={cat.chave} style={td}>{fmtDias(c.dias[cat.chave])}</td>)}
              <td style={td}>{fmtDias(c.dias_sem_registro)}</td>
              <td style={{ ...td, fontWeight: 700 }}>{fmtPct(c.ocupacao)}</td>
              <td style={td}>{fmtPct(c.ociosidade)}</td>
              <td style={{ ...td, textAlign: 'left' }}>
                {c.status && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconeStatus status={c.status} tamanho={13} />{STATUS[c.status].label}</span>}
              </td>
            </tr>
          ))}
          <tr>
            <td style={{ ...td, textAlign: 'left', fontWeight: 800 }}>Total do período</td>
            <td style={td} />
            <td style={{ ...td, fontWeight: 800 }}>{fmtDias(r.dias_uteis)}</td>
            {CATEGORIAS.map((cat) => <td key={cat.chave} style={{ ...td, fontWeight: 800 }}>{fmtDias(r.dias[cat.chave])}</td>)}
            <td style={{ ...td, fontWeight: 800 }}>{fmtDias(r.dias_sem_registro)}</td>
            <td style={{ ...td, fontWeight: 800 }}>{fmtPct(r.ocupacao)}</td>
            <td style={{ ...td, fontWeight: 800 }}>{fmtPct(r.ociosidade)}</td>
            <td style={td} />
          </tr>
        </tbody>
      </table>

      <Typography sx={{ fontWeight: 700, fontSize: 13, color: TINTA, mt: 3, mb: 1 }}>Evolução mensal</Typography>
      <table style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr><th style={{ ...th, textAlign: 'left' }}>Mês</th><th style={th}>Dias úteis</th><th style={th}>Taxa de ocupação</th></tr>
        </thead>
        <tbody>
          {dados.mensal.map((m) => (
            <tr key={`${m.ano}-${m.mes}`}>
              <td style={{ ...td, textAlign: 'left', fontWeight: m.selecionado ? 700 : 400 }}>{MESES[m.mes - 1]}/{m.ano}</td>
              <td style={td}>{m.ocupacao === null ? '—' : fmtDias(m.dias_uteis)}</td>
              <td style={td}>{fmtPct(m.ocupacao)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

// ─── Dica flutuante (tooltip) ────────────────────────────────────────────────
function Dica({ dica }) {
  if (!dica) return null;
  const largura = 250;
  const esquerda = Math.min(dica.x + 16, window.innerWidth - largura - 12);
  const topo = Math.min(dica.y + 16, window.innerHeight - 40 - (dica.conteudo.linhas.length * 22 + 40));
  return (
    <Box role="tooltip" sx={{
      position: 'fixed', left: Math.max(8, esquerda), top: Math.max(8, topo), width: largura, zIndex: 1500, pointerEvents: 'none',
      backgroundColor: 'white', borderRadius: 2, p: 1.5, boxShadow: '0 8px 28px rgba(15,23,42,0.18)', border: '1px solid rgba(15,23,42,0.1)',
    }}>
      <Typography sx={{ fontWeight: 700, fontSize: 13, color: TINTA, mb: 0.6 }}>{dica.conteudo.titulo}</Typography>
      {dica.conteudo.linhas.map((l, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
          {l.status ? <IconeStatus status={l.status} tamanho={12} />
            : l.traco ? <Box sx={{ width: 14, height: 3, borderRadius: 2, backgroundColor: l.traco }} />
              : l.cor ? <Box sx={{ width: 9, height: 9, borderRadius: '2px', backgroundColor: l.cor, border: '1px solid rgba(0,0,0,0.08)' }} />
                : <Box sx={{ width: 9 }} />}
          <Typography sx={{ fontSize: 12, color: TINTA_2, flex: 1 }}>{l.rotulo}</Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: TINTA, fontVariantNumeric: 'tabular-nums' }}>{l.valor}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── Filtro de período: escolhe o ano e clica nos meses ─────────────────────
// Vários meses somam juntos (a taxa é a soma dos dias ocupados ÷ soma dos dias úteis).
// Meses sem nenhum dado ficam apagados, mas ainda dá para clicar neles.
function FiltroPeriodo({ selecionados, disponiveis, resumo, aoMudar }) {
  const hoje = dayjs();
  const doAno = (chave) => Number(chave.slice(0, 4));
  const [anoVisto, setAnoVisto] = useState(
    selecionados.length ? doAno([...selecionados].sort().pop()) : hoje.year()
  );
  const temDados = new Set(disponiveis);
  const anos = Array.from(new Set([
    hoje.year(), anoVisto, ...disponiveis.map(doAno), ...selecionados.map(doAno),
  ])).sort((a, b) => b - a);

  const alternar = (chave) => {
    if (selecionados.includes(chave)) {
      if (selecionados.length === 1) return;   // sempre fica pelo menos um mês
      aoMudar(selecionados.filter((c) => c !== chave));
    } else {
      aoMudar([...selecionados, chave].sort());
    }
  };
  const escolher = (chaves, ano) => { setAnoVisto(ano); aoMudar(chaves); };

  const atalhos = [
    {
      texto: 'Mês atual',
      acao: () => escolher([chavePeriodo(hoje.year(), hoje.month() + 1)], hoje.year()),
    },
    {
      texto: 'Últimos 3 meses',
      acao: () => escolher(
        [2, 1, 0].map((i) => { const d = hoje.subtract(i, 'month'); return chavePeriodo(d.year(), d.month() + 1); }),
        hoje.year(),
      ),
    },
    {
      texto: `Ano todo (${anoVisto})`,
      acao: () => {
        const ate = anoVisto === hoje.year() ? hoje.month() + 1 : 12;
        escolher(Array.from({ length: ate }, (_, i) => chavePeriodo(anoVisto, i + 1)), anoVisto);
      },
    },
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <TextField
          select size="small" label="Ano" value={anoVisto}
          onChange={(e) => setAnoVisto(Number(e.target.value))}
          sx={{ minWidth: 104 }}
        >
          {anos.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
        </TextField>
        <Typography sx={{ fontSize: 12.5, color: '#5b6573' }}>
          Clique nos meses que quer ver
        </Typography>
      </Box>

      <Box
        role="group" aria-label={`Meses de ${anoVisto}`}
        sx={{
          display: 'grid', gap: 0.75, maxWidth: 720,
          gridTemplateColumns: { xs: 'repeat(4, 1fr)', sm: 'repeat(6, 1fr)', lg: 'repeat(12, 1fr)' },
        }}
      >
        {MESES_CURTO.map((nome, i) => {
          const chave = chavePeriodo(anoVisto, i + 1);
          const marcado = selecionados.includes(chave);
          const apagado = !temDados.has(chave);
          return (
            <Button
              key={chave} size="small" onClick={() => alternar(chave)}
              aria-pressed={marcado}
              aria-label={`${MESES[i]} de ${anoVisto}${apagado ? ' (sem dados)' : ''}`}
              variant={marcado ? 'contained' : 'outlined'}
              sx={{
                textTransform: 'capitalize', fontWeight: 700, fontSize: 13, minWidth: 0, py: 0.6,
                borderColor: marcado ? NAVY : '#cfd6de',
                color: marcado ? '#fff' : NAVY,
                backgroundColor: marcado ? NAVY : '#fff',
                opacity: apagado && !marcado ? 0.45 : 1,
                '&:hover': { backgroundColor: marcado ? '#0d2642' : '#f1f4f8', borderColor: NAVY },
              }}
            >
              {nome}
            </Button>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
        {atalhos.map((a) => (
          <Button key={a.texto} size="small" onClick={a.acao}
            sx={{ textTransform: 'none', fontWeight: 600, color: NAVY, fontSize: 12.5 }}>
            {a.texto}
          </Button>
        ))}
        <Typography sx={{ fontSize: 12.5, color: '#5b6573', ml: 1 }}>
          Mostrando: <strong>{resumo}</strong>
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Tela ────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { temAcesso, loading: carregandoAuth } = useAuth();
  const acessoLiberado = temAcesso('dashboard');
  const navigate = useNavigate();

  const [periodosSel, setPeriodosSel] = useState([chavePeriodo(dayjs().year(), dayjs().month() + 1)]);
  const [celulasSel, setCelulasSel] = useState([]);
  const [consultoresSel, setConsultoresSel] = useState([]);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [verTabela, setVerTabela] = useState(false);
  const [dica, setDica] = useState(null);
  const pedido = useRef(0);

  useEffect(() => {
    if (!acessoLiberado) { setCarregando(false); return; }
    const numero = ++pedido.current;
    setCarregando(true);
    setErro(false);
    const params = new URLSearchParams();
    if (periodosSel.length) params.set('periodos', periodosSel.join(','));
    if (celulasSel.length) params.set('celulas', celulasSel.join(','));
    if (consultoresSel.length) params.set('consultores', consultoresSel.join(','));
    api.get(`/ocupacao/dashboard/?${params.toString()}`)
      .then((res) => { if (numero === pedido.current) { setDados(res.data); setCarregando(false); } })
      .catch(() => { if (numero === pedido.current) { setErro(true); setCarregando(false); } });
  }, [periodosSel, celulasSel, consultoresSel, acessoLiberado]);

  const mostrarDica = (e, conteudo) => setDica({ x: e.clientX, y: e.clientY, conteudo });
  const esconderDica = () => setDica(null);
  const abrirConsultor = (c) => navigate(`/membros-equipe/${c.id}`);

  if (carregandoAuth) {
    return <Carregando />;
  }
  if (!acessoLiberado) {
    return <AcessoNegado mensagem="Você não tem acesso à Taxa de Ocupação. Fale com o Admin se precisar desse acesso." />;
  }

  // ── opções dos filtros ──
  const opcoes = dados?.opcoes || { celulas: [], consultores: [] };
  const opcoesPeriodo = dados
    ? dados.periodos_disponiveis.map((p) => chavePeriodo(p.ano, p.mes))
    : periodosSel;
  const consultoresDisponiveis = opcoes.consultores.filter((c) => celulasSel.length === 0 || celulasSel.includes(c.celula_id));

  const trocarCelulas = (ids) => {
    setCelulasSel(ids);
    if (ids.length) {
      const permitidos = new Set(opcoes.consultores.filter((c) => ids.includes(c.celula_id)).map((c) => c.id));
      setConsultoresSel((atual) => atual.filter((id) => permitidos.has(id)));
    }
  };

  // ── textos do destaque ──
  const periodoTexto = (() => {
    const lista = [...periodosSel].sort();
    if (lista.length === 0) return '';
    if (lista.length === 1) return rotuloPeriodoLongo(lista[0]);
    if (lista.length <= 3) return lista.map((c) => { const [a, m] = c.split('-'); return rotuloMesCurto(Number(a), Number(m)); }).join(', ');
    const seguidos = lista.every((c, i) => {
      if (i === 0) return true;
      const [a, m] = lista[i - 1].split('-').map(Number);
      return c === (m === 12 ? chavePeriodo(a + 1, 1) : chavePeriodo(a, m + 1));
    });
    if (!seguidos) return `${lista.length} meses selecionados`;
    const [a1, m1] = lista[0].split('-'); const [a2, m2] = lista[lista.length - 1].split('-');
    return `${rotuloMesCurto(Number(a1), Number(m1))} a ${rotuloMesCurto(Number(a2), Number(m2))} (${lista.length} meses)`;
  })();
  const escopoTexto = (() => {
    if (consultoresSel.length) return `${consultoresSel.length} consultor${consultoresSel.length === 1 ? '' : 'es'} selecionado${consultoresSel.length === 1 ? '' : 's'}`;
    if (celulasSel.length) return opcoes.celulas.filter((c) => celulasSel.includes(c.id)).map((c) => c.nome).join(', ');
    return 'Todas as células';
  })();

  const metas = dados?.metas || { verde: 72, amarelo: 65 };
  const consultores = dados?.consultores || [];
  const semDados = dados && dados.resumo.n_consultores === 0;

  // ── dados dos gráficos ──
  // Ocupação total: todas as categorias juntas. É só informação — a meta vale apenas para Consultoria.
  const itensOcupacao = [...consultores]
    .sort((a, b) => (b.ocupacao_total ?? -1) - (a.ocupacao_total ?? -1) || a.nome.localeCompare(b.nome))
    .map((c) => ({
      id: c.id, nome: c.nome, valor: c.ocupacao_total,
      cor: NAVY,
      tooltip: {
        titulo: c.nome,
        linhas: [
          { rotulo: 'Ocupação total', valor: fmtPct(c.ocupacao_total) },
          { rotulo: 'Dias úteis', valor: fmtDias(c.dias_uteis) },
          ...CATEGORIAS.map((cat) => ({ cor: cat.cor, rotulo: cat.label, valor: `${fmtDias(c.dias[cat.chave])} dias` })),
          { cor: SEM_REGISTRO.cor, rotulo: 'Sem registro', valor: `${fmtDias(c.dias_sem_registro)} dias` },
        ],
      },
    }));
  const itensOciosidade = [...consultores]
    .sort((a, b) => (b.ociosidade ?? -1) - (a.ociosidade ?? -1) || a.nome.localeCompare(b.nome))
    .map((c) => ({
      id: c.id, nome: c.nome, valor: c.ociosidade, cor: NAVY,
      tooltip: {
        titulo: c.nome,
        linhas: [
          { cor: SEM_REGISTRO.cor, rotulo: 'Ociosidade', valor: fmtPct(c.ociosidade) },
          { rotulo: 'Dias sem registro', valor: fmtDias(c.dias_sem_registro) },
          { rotulo: 'Dias úteis', valor: fmtDias(c.dias_uteis) },
        ],
      },
    }));
  const maximo = Math.max(100, ...consultores.map((c) => c.ocupacao_total || 0), ...consultores.map((c) => c.ociosidade || 0));
  const escala = Math.ceil(maximo / 25) * 25;

  return (
    <Box>
      <PageHeader titulo="Taxa de Ocupação" />

      {/* ── filtros: primeiro o período (ano + meses), depois célula e consultor ── */}
      <FiltroPeriodo
        selecionados={periodosSel} disponiveis={opcoesPeriodo} resumo={periodoTexto}
        aoMudar={(lista) => setPeriodosSel(lista.length ? lista : [chavePeriodo(dayjs().year(), dayjs().month() + 1)])}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', mb: 2.5 }}>
        <Autocomplete
          multiple size="small" disableCloseOnSelect limitTags={1}
          options={opcoes.celulas.map((c) => c.id)}
          value={celulasSel}
          getOptionLabel={(id) => opcoes.celulas.find((c) => c.id === id)?.nome || ''}
          onChange={(_, v) => trocarCelulas(v)}
          renderInput={(params) => <TextField {...params} label="Célula" placeholder={celulasSel.length ? '' : 'Todas'} />}
          sx={{ minWidth: { xs: '100%', sm: 200 }, flex: { sm: '0 1 260px' } }}
        />
        <Autocomplete
          multiple size="small" disableCloseOnSelect limitTags={1}
          options={consultoresDisponiveis.map((c) => c.id)}
          value={consultoresSel}
          getOptionLabel={(id) => opcoes.consultores.find((c) => c.id === id)?.nome || ''}
          onChange={(_, v) => setConsultoresSel(v)}
          renderInput={(params) => <TextField {...params} label="Consultor" placeholder={consultoresSel.length ? '' : 'Todos'} />}
          sx={{ minWidth: { xs: '100%', sm: 220 }, flex: { sm: '0 1 300px' } }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', minHeight: 40 }}>
          {(celulasSel.length > 0 || consultoresSel.length > 0) && (
            <Button size="small" onClick={() => { setCelulasSel([]); setConsultoresSel([]); }}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#e94560', fontSize: 12.5 }}>
              Limpar filtros
            </Button>
          )}
        </Box>
      </Box>

      {erro && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          Não foi possível carregar a Taxa de Ocupação. Atualize a página; se continuar, avise o TI.
        </Alert>
      )}

      {!dados && carregando && <Carregando />}

      {dados && (
        <Box sx={{ opacity: carregando ? 0.55 : 1, transition: 'opacity 0.2s ease' }}>
          <Destaque resumo={dados.resumo} metas={metas} periodoTexto={periodoTexto} escopoTexto={escopoTexto} />

          {semDados ? (
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography sx={{ fontWeight: 700, color: TINTA, mb: 0.5 }}>Nenhum registro de ocupação neste filtro.</Typography>
                <Typography sx={{ color: TINTA_2, fontSize: 13.5 }}>
                  Os números aparecem conforme a equipe lança os dias na Agenda. Escolha outro período ou limpe os filtros.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <>
              <Indicadores resumo={dados.resumo} />

              <ConsultoriaPorConsultor
                consultores={consultores} resumo={dados.resumo} metas={metas}
                aoMostrar={mostrarDica} aoEsconder={esconderDica} aoClicar={abrirConsultor} />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <CartaoGrafico
                  titulo="Ocupação por consultor"
                >
                  <RankingBarras itens={itensOcupacao} escalaMax={escala}
                    aoMostrar={mostrarDica} aoEsconder={esconderDica} aoClicar={abrirConsultor} />
                </CartaoGrafico>

                <CartaoGrafico
                  titulo="Ociosidade por consultor"
                  subtitulo="Dias sem registro na Agenda ÷ dias úteis · do maior para o menor"
                >
                  <RankingBarras itens={itensOciosidade} escalaMax={escala}
                    aoMostrar={mostrarDica} aoEsconder={esconderDica} aoClicar={abrirConsultor} />
                </CartaoGrafico>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '7fr 5fr' }, gap: 2, mb: 2, alignItems: 'start' }}>
                <CartaoGrafico
                  titulo="Como cada consultor usou os dias"
                  subtitulo="Composição percentual dos dias úteis"
                  legenda={[...CATEGORIAS, { chave: 'sr', label: SEM_REGISTRO.label, cor: SEM_REGISTRO.cor }].map((c) => (
                    <ItemLegenda key={c.chave} cor={c.cor} texto={c.label} />
                  ))}
                >
                  <Composicao consultores={consultores} aoMostrar={mostrarDica} aoEsconder={esconderDica} aoClicar={abrirConsultor} />
                </CartaoGrafico>

                <CartaoGrafico
                  titulo="Taxa de ocupação por mês"
                  subtitulo="Últimos 12 meses até o último mês escolhido · respeita Célula e Consultor"
                >
                  <Evolucao mensal={dados.mensal} metas={metas} aoMostrar={mostrarDica} aoEsconder={esconderDica} />
                </CartaoGrafico>
              </Box>

              <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: TINTA }}>Dados em tabela</Typography>
                    <Button size="small" onClick={() => setVerTabela((v) => !v)} sx={{ textTransform: 'none', fontWeight: 600, color: NAVY }}>
                      {verTabela ? 'Esconder tabela' : 'Mostrar tabela'}
                    </Button>
                  </Box>
                  {verTabela && <Box sx={{ mt: 1.5 }}><TabelaDados dados={dados} aoClicar={abrirConsultor} /></Box>}
                </CardContent>
              </Card>
            </>
          )}
        </Box>
      )}

      <Dica dica={dica} />
    </Box>
  );
}
